import type { Color } from "../../types";
import type { HasAverageColor } from "../HasAverageColor";
import type { HasBackgroundColor } from "../HasBackgroundColor";
import type { Viewport } from "../Viewport";
import type { VirtualViewportPixelAccessor } from "../VirtualViewportPixelAccessor";
import type { VirtualPixelDecorator } from "./VirtualPixelDecorator";

/**
 * Abstract virtual pixel decorator.
 */
export abstract class AbstractVirtualPixelDecorator<
  DecoratedType extends VirtualViewportPixelAccessor &
    HasAverageColor &
    HasBackgroundColor
> implements VirtualPixelDecorator<DecoratedType>
{
  /**
   * @inheritDoc
   */
  readonly width: number;

  /**
   * @inheritDoc
   */
  readonly height: number;

  /**
   * Origin X offset in virtual viewport.
   * @protected
   */
  protected offsetX: number;

  /**
   * Origin Y offset in virtual viewport.
   * @protected
   */
  protected offsetY: number;

  /**
   * BaseVirtualPixelDecorator constructor.
   *
   * @param decorated
   */
  constructor(protected readonly decorated: DecoratedType) {
    this.width = this.decorated.width;
    this.height = this.decorated.height;
    ({ x1: this.offsetX, y1: this.offsetY } = this.decorated.getViewport());
  }

  /**
   * @inheritDoc
   */
  abstract getVirtualPixelColor(x: number, y: number): Color;

  /**
   * @inheritDoc
   */
  getPixelColor(x: number, y: number): Color {
    x = Math.floor(x - this.offsetX);
    y = Math.floor(y - this.offsetY);

    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      return this.decorated.getPixelColor(x, y);
    }

    return this.getVirtualPixelColor(x, y);
  }

  /**
   * @inheritDoc
   */
  setPixelColor(x: number, y: number, color: Color): void {
    x = Math.floor(x - this.offsetX);
    y = Math.floor(y - this.offsetY);

    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      this.decorated.setPixelColor(x, y, color);
    }
  }

  /**
   * @inheritDoc
   */
  getQuantumRange(): number {
    return this.decorated.getQuantumRange();
  }

  /**
   * @inheritDoc
   */
  getViewport(): Viewport {
    return this.decorated.getViewport();
  }

  /**
   * @inheritDoc
   */
  getAverageColor(): Color {
    return this.decorated.getAverageColor();
  }

  /**
   * @inheritDoc
   */
  getBackgroundColor(): Color {
    return this.decorated.getBackgroundColor();
  }

  /**
   * @inheritDoc
   */
  getDecorated(): DecoratedType {
    return this.decorated;
  }
}
